import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValue, useTransform, animate } from "framer-motion";
import { Heart, X, Sparkles, MapPin, Info, RotateCcw, Eye, Lock } from "lucide-react";
import { StreamingOnlyLabel } from "@/components/FoundingBadge";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { useAuth } from "@/context/AuthContext";
import { isPaid } from "@/lib/membership";
import { toast } from "sonner";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1637059880830-59a90102de77";
const THRESHOLD = 110;

const Card = ({ profile, top, onDecide, blurred, canReveal, flingRef }) => {
  const [revealed, setRevealed] = useState(false);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-240, 240], [-14, 14]);
  const admireOpacity = useTransform(x, [30, THRESHOLD], [0, 1]);
  const passOpacity = useTransform(x, [-THRESHOLD, -30], [1, 0]);

  const gone = useRef(false);
  const fling = (dir, kind) => {
    if (gone.current) return;
    gone.current = true;
    animate(x, dir * 600, { duration: 0.28 }).then(() => onDecide(kind || (dir > 0 ? "admire" : "pass")));
  };
  const onDragEnd = (_, info) => {
    if (info.offset.x > THRESHOLD || info.velocity.x > 600) fling(1);
    else if (info.offset.x < -THRESHOLD || info.velocity.x < -600) fling(-1);
    else animate(x, 0, { type: "spring", stiffness: 400, damping: 30 });
  };
  if (top) flingRef.current = fling;

  return (
    <motion.div
      data-testid={top ? "swipe-card-top" : "swipe-card-next"} data-profile-id={profile.id}
      className="absolute inset-0 rounded-3xl overflow-hidden border border-white/10 bg-surface shadow-2xl touch-pan-y select-none"
      style={{ x, rotate, zIndex: top ? 2 : 1 }}
      drag={top ? "x" : false} dragElastic={0.9} dragConstraints={{ left: 0, right: 0 }} onDragEnd={onDragEnd}
      initial={top ? false : { scale: 0.94, y: 14 }} animate={{ scale: top ? 1 : 0.94, y: top ? 0 : 14 }}
      exit={{ opacity: 0, transition: { duration: 0.15 } }}
    >
      <img src={profile.photo_url || FALLBACK_IMG} alt={profile.display_name} draggable={false}
        className={`w-full h-full object-cover pointer-events-none transition-[filter] duration-500 ${blurred && !revealed ? "blur-2xl scale-110" : ""}`} />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent pointer-events-none" />
      {top && blurred && !revealed && (
        <div className="absolute inset-0 flex items-center justify-center pb-24">
          {canReveal ? (
            <button data-testid="swipe-reveal" onClick={() => setRevealed(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/50 backdrop-blur-md border border-white/20 text-[11px] uppercase tracking-[0.2em]">
              <Eye size={14} /> Tap to reveal
            </button>
          ) : (
            <Link to="/premium" data-testid="swipe-upgrade"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/50 backdrop-blur-md border border-gold/40 text-gold text-[11px] uppercase tracking-[0.2em]">
              <Lock size={14} /> Upgrade to see photos
            </Link>
          )}
        </div>
      )}
      {top && (
        <>
          <motion.span style={{ opacity: admireOpacity }} className="absolute top-8 left-6 -rotate-12 px-4 py-1.5 rounded-xl border-2 border-gold text-gold text-xl uppercase tracking-[0.2em] font-semibold">Admire</motion.span>
          <motion.span style={{ opacity: passOpacity }} className="absolute top-8 right-6 rotate-12 px-4 py-1.5 rounded-xl border-2 border-white/70 text-white/80 text-xl uppercase tracking-[0.2em] font-semibold">Pass</motion.span>
        </>
      )}
      <div className="absolute bottom-0 inset-x-0 p-5 pointer-events-none">
        <p className="font-display text-4xl font-light flex items-center gap-2">
          <span>{profile.display_name}<span className="text-2xl text-text-muted">, {profile.age}</span></span>
          <VerifiedBadge user={profile} size="sm" />
        </p>
        {profile.location && <p className="text-sm text-text-muted flex items-center gap-1 mt-1"><MapPin size={13} /> {profile.location}{profile.distance_km != null && ` · ${profile.distance_km} km`}</p>}
        {profile.is_creator && <div className="mt-2"><StreamingOnlyLabel user={profile} testId={`swipe-streaming-only-${profile.id}`} /></div>}
        {profile.tagline && <p className="font-display italic text-gold text-lg mt-2 line-clamp-2">"{profile.tagline}"</p>}
        {!!profile.kinks?.length && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {profile.kinks.slice(0, 3).map((k) => <span key={k} className="text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border border-white/15 bg-black/30">{k}</span>)}
          </div>
        )}
      </div>
      {top && (
        <Link to={`/profile/${profile.id}`} data-testid="swipe-view-profile" aria-label="View full profile"
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center text-text-main">
          <Info size={18} />
        </Link>
      )}
    </motion.div>
  );
};

export const SwipeDeck = ({ profiles, onLike, onSuperLike }) => {
  const { user } = useAuth();
  const paid = isPaid(user);
  const [idx, setIdx] = useState(0);
  const flingRef = useRef(null);
  const stageRef = useRef(null);
  const [stageH, setStageH] = useState(null);
  const deck = profiles.slice(idx, idx + 2);

  // Fit the card to the space left between its top edge and the action buttons + tab bar.
  useLayoutEffect(() => {
    const fit = () => {
      if (!stageRef.current) return;
      const top = stageRef.current.getBoundingClientRect().top + window.scrollY;
      setStageH(Math.max(320, Math.min(640, window.innerHeight - top - 200)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [deck.length]);

  const [last, setLast] = useState(null);
  const decide = (p, choice) => {
    if (choice === "admire" && !p.liked_by_me) onLike(p);
    if (choice === "super") onSuperLike(p);
    setLast({ p, choice, sentLike: choice === "admire" && !p.liked_by_me });
    setIdx((i) => i + 1);
  };
  const undo = () => {
    if (!last) return;
    if (last.sentLike) onLike(last.p);
    if (last.choice === "super") toast("Card restored. Your Super Admire was already delivered.");
    setIdx((i) => Math.max(0, i - 1));
    setLast(null);
  };
  const tap = (dir, kind) => flingRef.current?.(dir, kind);
  const superTap = () => tap(1, "super");

  if (!deck.length) {
    return (
      <div data-testid="swipe-empty" className="text-center py-20">
        <p className="font-display italic text-2xl text-text-muted">You've seen everyone for now.</p>
        {last && (
          <button data-testid="swipe-undo-empty" onClick={undo} className="mt-6 mr-3 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-text-muted text-xs uppercase tracking-[0.2em]">
            <RotateCcw size={14} /> Undo last
          </button>
        )}
        <button data-testid="swipe-restart" onClick={() => setIdx(0)} className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-gold/40 text-gold text-xs uppercase tracking-[0.2em]">
          <RotateCcw size={14} /> Start over
        </button>
      </div>
    );
  }

  return (
    <div data-testid="swipe-deck">
      <div ref={stageRef} className="relative w-full mx-auto" style={{ height: stageH ? `${stageH}px` : "calc(100dvh - 23rem)" }}>
        <AnimatePresence>
          {deck.map((p, i) => (
            <Card key={p.id} profile={p} top={i === 0} onDecide={(c) => decide(p, c)} blurred={p.discreet_mode || !paid} canReveal={paid} flingRef={flingRef} />
          )).reverse()}
        </AnimatePresence>
      </div>
      <div className="flex items-center justify-center gap-4 mt-5">
        <button data-testid="swipe-undo" onClick={undo} disabled={!last} aria-label="Undo last swipe"
          className="w-12 h-12 rounded-full border border-white/15 text-text-muted flex items-center justify-center active:scale-90 transition-[transform,opacity] disabled:opacity-30"><RotateCcw size={18} /></button>
        <button data-testid="swipe-pass" onClick={() => tap(-1)} aria-label="Pass"
          className="w-16 h-16 rounded-full border border-white/20 text-text-main flex items-center justify-center active:scale-90 transition-transform"><X size={26} /></button>
        <button data-testid="swipe-super" onClick={superTap} aria-label="Super admire"
          className="w-12 h-12 rounded-full border border-gold/50 text-gold flex items-center justify-center active:scale-90 transition-transform"><Sparkles size={18} /></button>
        <button data-testid="swipe-admire" onClick={() => tap(1)} aria-label="Admire"
          className="w-16 h-16 rounded-full bg-wine text-text-main flex items-center justify-center active:scale-90 transition-transform"><Heart size={26} /></button>
      </div>
      <p className="text-center text-[11px] text-text-muted mt-3">Swipe right to admire · left to pass</p>
    </div>
  );
};
